import moment from 'moment';
import {
  PARAM_Q,
  PARAM_DATE,
  PARAM_NIGHT,
  PARAM_AUTOCOMPLETE,
  PARAM_APARTMENT,
  PARAM_HOTEL_DIRECT_ID,
  PARAM_GEO_CODE,
  PARAM_GEO_TYPE,
  PARAM_SORT,
  HOTEL_TYPE,
  AMENITIES,
  PRICE_RANGE,
  ROOM_ID,
  RATE_ID,
  APARTMENT_ID,
  HOTEL_ID,
  RADIUS,
  UNIT,
  PARAM_CURRENCY,
  PARAM_COUNTRY,
  PARAM_OFFER,
} from './constants';
import {generateNewApartmentObj, parseApartment, serializeApartment} from './utils/apartmentParser';

/**
 * Schema definition for all supported URL parameters.
 */
export const urlSchema = {
  // q=Paris
  [PARAM_Q]: {
    key: 'q',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_HOTEL_DIRECT_ID]: {
    key: 'hotelDirectId',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_GEO_CODE]: {
    key: 'geoCode',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_GEO_TYPE]: {
    key: 'geoType',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  // dateIn=2026-01-13
  [PARAM_DATE]: {
    key: 'dateIn',
    defaultValue: '',
    validate: (val) => {
      if (!val || !/^\d{4}-\d{2}-\d{2}$/.test(val)) return false;
      const date = moment(val, 'YYYY-MM-DD', true);
      return date.isValid() && !date.startOf('day').isBefore(moment().startOf('day'));
    },
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  // nbNights=3
  [PARAM_NIGHT]: {
    key: window?.drupalSettings?.ngaEnabled ? 'lengthOfStay.value' : 'nbNight',
    backupKey: window?.drupalSettings?.ngaEnabled ? 'nbNight' : 'lengthOfStay.value',
    defaultValue: '',
    validate: (val) => typeof val === 'number' && Number.isInteger(val) && val >= 1,
    parse: (val) => {
      if (val === null || val === undefined || val === '') {
        return undefined;
      }
      const parsed = parseInt(String(val), 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    },
    serialize: (val) => {
      if (typeof val === 'number' && Number.isInteger(val) && val >= 1) {
        return String(val);
      }
      return null;
    },
  },
  // isAutocomplete=true
  [PARAM_AUTOCOMPLETE]: {
    key: 'isAutocomplete',
    defaultValue: false,
    validate: (val) => typeof val === 'boolean',
    parse: (val) => val === 'true',
    serialize: (val) => (val ? 'true' : null), // null removes param from URL
  },
  // apartment[]=2,1:2,,FVeO6gkunH
  [PARAM_APARTMENT]: {
    key: 'apartment[]',
    defaultValue: [generateNewApartmentObj()],
    validate: (val) => Array.isArray(val),
    // isMulti: true indicates we should use params.getAll()
    isMulti: true,
    parse: (values) => {
      if (!Array.isArray(values) || values.length === 0) return undefined;
      return values.map(parseApartment).filter(Boolean);
    },
    serialize: (values) => {
      if (!Array.isArray(values)) return [];
      return values.map(serializeApartment);
    },
  },
  [RADIUS]: {
    key: 'radius',
    defaultValue: '',
    validate: (val) => typeof val === 'number',
    parse: (val) => val ? parseInt(val, 10) : undefined,
    serialize: (val) => val,
  },
  [UNIT]: {
    key: 'unit',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val,
    serialize: (val) => val,
  },
  [PARAM_SORT]: {
    key: 'sort',
    defaultValue: '',
    validate: (val) => typeof val === 'number',
    parse: (val) => val ? parseInt(val, 10) : undefined,
    serialize: (val) => val,
  },
  [HOTEL_TYPE]: {
    key: 'hotel_type',
    defaultValue: [],
    validate: (val) => Array.isArray(val),
    parse: (val) => val ? val.split(',') : [],
    serialize: (values) => values.join(','),
  },
  [AMENITIES]: {
    key: 'amenities',
    defaultValue: [],
    validate: (val) => Array.isArray(val),
    parse: (val) => val ? val.split(',') : [],
    serialize: (values) => values.join(','),
  },
  [PRICE_RANGE]: {
    key: 'price_range_dynamic',
    defaultValue: [],
    validate: (val) => Array.isArray(val),
    parse: (val) => {
      if (!val) return undefined;
      const [min, max] = val.split(',').map(val => parseInt(val, 10));
      return {min, max};
    },
    serialize: ({min, max}) => !!min || !!max ? `${min ? min : ''},${max ? max : ''}` : '',
  },
  [HOTEL_ID]: {
    key: 'hotel_id',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [ROOM_ID]: {
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [RATE_ID]: {
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [APARTMENT_ID]: {
    key: 'apartment_id',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_CURRENCY]: {
    key: 'currency',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_COUNTRY]: {
    key: 'country',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
  [PARAM_OFFER]: {
    key: 'offer_id',
    defaultValue: '',
    validate: (val) => typeof val === 'string',
    parse: (val) => val || '',
    serialize: (val) => val,
  },
};
